import { FC, useState } from 'react';
import { Button, Dropdown } from 'antd';
import { TbDotsVertical, TbTrash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { InboxItem } from '@/services/database';
import DropdownPanel, { DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { useInboxNotificationStore } from '@/stores';

interface NotificationActionsDropdownProps {
    disabled?: boolean;
    item: InboxItem;
}

const NotificationActionsDropdown: FC<NotificationActionsDropdownProps> = ({ disabled, item }) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(false);
    const deleteItem = useInboxNotificationStore((state) => state.deleteItem);
    const deleting = useInboxNotificationStore((state) => state.deletingItemIds.includes(item.id));

    const handleDelete = async () => {
        if (deleting || disabled) {
            return;
        }
        try {
            await deleteItem(item);
            setOpen(false);
            toast.success('Notification deleted');
        } catch {
            // The notification store reports the exception; keep the item available for retry.
            toast.error('Could not delete notification');
        }
    };

    return (
        <SmallTooltip title='Notification actions' placement='right' padding='0'>
            <Dropdown
                disabled={disabled || deleting}
                open={open}
                onOpenChange={setOpen}
                trigger={['click']}
                placement='bottomRight'
                popupRender={() => (
                    <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                        <DropdownPanel width='14rem'>
                            <DropdownPanelMenu
                                items={[
                                    {
                                        danger: true,
                                        disabled: deleting || disabled,
                                        icon: <TbTrash size='1rem' />,
                                        key: 'delete',
                                        label: deleting ? 'Deleting notification…' : 'Delete Notification',
                                        onClick: () => void handleDelete(),
                                    },
                                ]}
                            />
                        </DropdownPanel>
                    </div>
                )}
            >
                <Button
                    aria-label='Notification actions'
                    disabled={disabled || deleting}
                    icon={<TbDotsVertical size='1rem' style={{ color: token.colorTextTertiary }} />}
                    loading={deleting}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    size='small'
                    style={{ height: '1.25rem', width: '1.25rem' }}
                    type='text'
                />
            </Dropdown>
        </SmallTooltip>
    );
};

export default NotificationActionsDropdown;
